import { Tabs } from '@/components/ui/tabs';
import { BulkDashboardTab, setActiveTab } from '@common-reducers/CampaignReducer';
import { useAnalyticsService } from '@common-reducers/hooks/use-analytics-service';
import { AnalyticsService } from '@common-services/analytics-service';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { Megaphone, Users, MessageSquare } from 'lucide-react';
import React from 'react'
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import { useRouter } from '@common-reducers/hooks/use-router';
import { isExtensionContextOrWhatsappView } from '@common-services/detect-context';
import { tabToPath } from '@common/utils/campaign-routes';
import { useIsMobile } from '@common/hooks/use-is-mobile';

export default function BtBulkMangementTabs() {
    const dispatch = useAppDispatch()
    const router = useRouter();
    const activeTab = useAppSelector((state) => state.CampaignReducer.activeTab);
    const analyticsService: AnalyticsService = useAnalyticsService();
    const { t } = useTranslation(namespace);
    const isMobile = useIsMobile();

    const tabsData = [
        {
            label: t('campaign.manageCampaigns'),
            value: "manageCampaigns",
            icon: Megaphone,
        },
        {
            label: t('campaign.manageAudiences'),
            value: "manageAudiences",
            icon: Users,
        },
        {
            label: t('campaign.manageTemplates'),
            value: "manageTemplates",
            icon: MessageSquare,
        },
    ];

    const handleBulkCampaignTabChange = (newValue: string) => {
        analyticsService.event('bulk_dashboard_tab_change', {
            category: 'bulk',
            action: newValue
        });

        dispatch(setActiveTab(newValue as BulkDashboardTab));

        // Website only: reflect the active tab in the URL so it is deep-linkable.
        // Skipped in the extension / WhatsApp view, where driving the router crashes it.
        if (!isExtensionContextOrWhatsappView()) {
            router.navigate(tabToPath(newValue as BulkDashboardTab));
        }
    }

    return (
        <div className={cn("flex flex-row justify-center w-full mb-6", isMobile && "px-2")}>
            <Tabs value={activeTab} onValueChange={handleBulkCampaignTabChange} className="w-full max-w-4xl">
                <div className={cn("flex gap-4", isMobile && "gap-2 overflow-x-auto pb-1")}>
                    {tabsData.map((tab) => {
                        const Icon = tab.icon;
                        const isActive = activeTab === tab.value;
                        return (
                            <button
                                key={tab.value}
                                onClick={() => handleBulkCampaignTabChange(tab.value)}
                                className={cn(
                                    "flex items-center gap-3 px-6 py-4 rounded-xl transition-all font-medium",
                                    isMobile && "gap-2 px-3 py-2.5 text-sm whitespace-nowrap shrink-0",
                                    isActive
                                        ? "bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 shadow-sm border border-blue-200 dark:border-blue-800"
                                        : "bg-slate-50 dark:bg-slate-800/50 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                                )}
                            >
                                <Icon className="w-5 h-5" />
                                <span>{tab.label}</span>
                            </button>
                        );
                    })}
                </div>
            </Tabs>
        </div>
    )
}